import { useFeatureExtractionContext } from '@mobile-mapping/providers';
import {
  FilterHandlerRecord,
  useProjectDataPanel,
} from '@trimble/project-data-panel-ui';
import {
  SelectionTree,
  SelectionTreeProps,
  SortableKeyOf,
  SortDirection,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { createMemo, JSX, Show } from 'solid-js';
import {
  getLayerIdFromNodeId,
  WorkspaceTreeItem,
} from './workspace-tree-nodes';
import { ProjectDataFilterMode } from './project-data.types';

export interface WorkspacesSelectionTreeContainerProps {
  nodes: TreeNodeDefinition<WorkspaceTreeItem>[];
  childrenLoadStatus?: SelectionTreeProps<WorkspaceTreeItem>['childrenLoadStatus'];
  filterText?: string;
  sortBy: SortableKeyOf<TreeNodeDefinition<WorkspaceTreeItem>>;
  sortDirection: SortDirection;
  filterMode?: ProjectDataFilterMode;
}

export const WorkspacesSelectionTreeContainer = (
  props: WorkspacesSelectionTreeContainerProps
): JSX.Element => {
  const featureExtraction = useFeatureExtractionContext();
  const { filterText, filterMode } = useProjectDataPanel();

  const isLayerNodeVisible = (nodeId: string): boolean => {
    const layerId = getLayerIdFromNodeId(nodeId);
    return layerId !== undefined && featureExtraction.isLayerVisible(layerId);
  };

  const handleVisibilityToggle = (nodeId: string): void => {
    const layerId = getLayerIdFromNodeId(nodeId);
    if (
      layerId === undefined ||
      featureExtraction.layersLoadStatus() !== 'success'
    ) {
      return;
    }
    featureExtraction.toggleLayerVisibility(layerId);
  };

  const isNodeSelected = (nodeId: string): boolean =>
    getLayerIdFromNodeId(nodeId) ===
    featureExtraction.editingState.selectedLayerId;

  const handleNodeSelect = (
    _nodeId: string,
    node: TreeNodeDefinition<WorkspaceTreeItem>
  ): void => {
    if (node.data?.kind === 'layer' && node.data.layerId !== undefined) {
      featureExtraction.setSelectedLayerId(node.data.layerId);
    }
  };

  const canRenderTree = createMemo(
    () => props.childrenLoadStatus === 'error' || props.nodes.length > 0
  );

  const filterHandlers: FilterHandlerRecord<WorkspaceTreeItem> = {
    all: { filter: () => true },
    active: {
      filter: (node) => isLayerNodeVisible(node.id),
    },
    archived: { filter: () => true },
  };

  return (
    <div data-testid="workspaces-selection-tree">
      <Show when={canRenderTree()} keyed>
        {(_ready) => (
          <SelectionTree
            multiSelect={false}
            childrenLoadStatus={props.childrenLoadStatus ?? 'pending'}
            nodes={props.nodes}
            sortBy={props.sortBy}
            sortDirection={props.sortDirection}
            filterText={props.filterText ?? filterText()}
            filterMode={props.filterMode ?? filterMode()}
            filterHandlers={filterHandlers}
            isNodeLoading={() => false}
            isNodeVisible={isLayerNodeVisible}
            onVisibilityToggle={handleVisibilityToggle}
            isNodeSelected={isNodeSelected}
            onNodeSelect={handleNodeSelect}
          />
        )}
      </Show>
    </div>
  );
};
